import React, { useCallback, useState } from 'react'
import type { PopulatedAppointment } from 'src/hooks/usePopulatedAppointments'
import { Arrow } from 'components/Utility/Arrow/Arrow'
import { Summary, type SummaryService } from 'components/Appointment/ClientsSummary/ClientsSummary'
import { DateInfo } from 'components/Profile/VisitInfo/DateInfo'
import { SalonInfo } from 'components/Profile/VisitInfo/SalonInfo'
import styles from './Visit.module.scss'

export type VisitAction = {
  name: string,
  title: string,
  onClick: (e: React.MouseEvent) => void,
}

type VisitCardProps = {
  visit: PopulatedAppointment,
  icon: React.ReactNode,
  actions: VisitAction[],
  discountAmount?: number,
  className?: string,
}

/** A collapsible visit row in the account, shared by upcoming and past visits */
export const VisitCard: React.FC<VisitCardProps> = (props) => {
  const { visit, icon, actions, discountAmount, className } = props
  const [expanded, setExpanded] = useState(false)
  const toggle = useCallback(() => setExpanded((value) => !value), [])

  return (
    <div className={className}>
      <div className={styles.header} onClick={toggle}>
        {icon}
        <div className={styles.headerInfo}>
          <DateInfo className={styles.dateInfo} date={visit.date} time={visit.time} />
          <SalonInfo className={styles.salonInfo} salon={visit.salon} />
        </div>
        <div className={styles.expandButton}>
          <Arrow direction={expanded ? 'down' : 'right'} isDark isSmall />
        </div>
      </div>
      {expanded && (
        <div className={styles.moreInfo}>
          <Summary
            className={styles.summary}
            itemClassName={styles.clientSummary}
            // Services removed from the catalogue have no prices or name; the summary copes with that
            clients={[visit.services as unknown as SummaryService[]]}
            discountAmount={discountAmount}
            salon={visit.salon}
          />
          <div className={styles.actions}>
            {actions.map((action) => (
              <div key={action.name} className={styles.action} data-name={action.name} onClick={action.onClick}>
                <i className={styles.actionIcon} />
                <div className={styles.actionTitle}>{action.title}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
